import React, { useEffect, useMemo, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button, Col, Row, Typography } from "antd";
import styled from "styled-components";
import { PageWrapper } from "../../../components/UI/PageWrapper/PageWrapper";
import { CustomCard } from "../../../components/UI/AntCustom/CustomCard";
import { CustomSearch } from "../../../components/UI/AntCustom/CustomSearch";
import { CustomResult } from "../../../components/UI/AntCustom/CustomResult";
import { CustomButton } from "../../../components/UI/AntCustom/CustomButton";
import { CustomDrawer } from "../../../components/UI/AntCustom/CustomDrawer";
import { CustomModal } from "../../../components/UI/AntCustom/CustomModal";
import { Loader } from "../../../components/UI/Loader/Loader";
import debounce from "lodash/debounce";
import { AirportApi } from "../../../API/ApiClasses/AirportApi";
import { AirportApiAirportListRequest, AirportApiGetAirportFromAviapagesRequest, PageModel } from "../../../proto/generated/api_entities_pb";
import { CommonFormMessages } from "../../../intl/commonMessages/CommonFormMessages";
import { InitialPage } from "../../../utils/constans/pagination/pagination";
import { useAppSelector } from "../../../store/store";
import { useNavigate } from "react-router";
import { pathFromRoute } from "../../../utils/helpers/acl/pathFromRoute";
import { Route } from "../../../config/acl/routes/Route";
import { mediaScreenSizes, screenSizes } from "../../../utils/constans/styles/screenSizes";
import { useWindowSize } from "../../../hooks/useWindowSize";
import WarningIcon from "../../../media/icons/warning.svg?react";
import { UserStatus } from "../../../utils/constans/statuses/userStatuses";

const AirportDataHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #ededed;
`;

const DownloadButton = styled(Button)`
  min-width: 100px;
  height: 35px;
  padding: 0 12px;
  border-radius: 5px;
  border: 1px solid #1890ff;
  color: #1890ff;
  background: #ffffff;
`;

const SearchRow = styled(Row)`
  margin-top: 16px;
`;

const SearchError = styled(Typography.Text)`
  display: block;
  margin-top: 12px;
`;

const AirportList = styled.div`
  margin-top: 16px;
`;

const AirportListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ededed;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #fafafa;
  }

  @media (max-width: ${mediaScreenSizes.mobile}) {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    column-gap: 12px;
    row-gap: 4px;
    align-items: center;
  }
`;

const AirportLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 300px;
  width: 100%;
  min-width: 0;
  color: #000;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    grid-column: 1;
    grid-row: 1;
    max-width: none;
  }
`;

const AirportCodes = styled.div`
  font-weight: 600;
  white-space: nowrap;
`;

const AirportName = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AirportMiddle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding-left: 100px;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    grid-column: 1;
    grid-row: 2;
    padding-left: 0;
  }
`;

const AirportCountry = styled.span`
  font-weight: 600;
  color: #000;
`;

const AirportUtc = styled.span`
  color: #000;
  font-weight: 400;
`;

const AirportRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 24px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    grid-column: 2;
    grid-row: 1 / span 2;
    justify-self: end;
  }
`;

const AirportArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 23px;
  height: 23px;
  color: #000000;
  font-size: 23px;
  line-height: 1;
`;

const AiportDrawer = styled(CustomDrawer)`
  @media (max-width: 1032px) {
    width: 100%;
    
    .ant-drawer-body {
      padding: 16px 32px 16px 32px;
    }

    .ant-drawer-close{
      right: 26px;
      top: 28px;
    }
  }

  @media (max-width: ${mediaScreenSizes.mobile}) {
    .ant-drawer-body {
      margin-top: 45px;
    }

    .ant-result.ant-result-info {
      padding: 24px 16px;
    }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const DrawerTitle = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #000;
`;

const DrawerInfoCard = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
    gap: 16px;
  }
`;

const DrawerInfoLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 260px;
  flex-grow: 1;
  flex: 1 1 0px;
`;

const DrawerInfoName = styled.div`
  font-weight: 600;
  color: #000;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.2px;
`;

const DrawerInfoMeta = styled.div`
  color: #666;
  font-size: 12px;
  color: #808080;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.14px;
`;

const DrawerInfoRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #000;
  font-size: 12px;
  flex-grow: 1;
  min-width: 0;
  flex: 1 1 0px;
`;

const DrawerInfoLabel = styled.span`
  color:  #808080;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.16px;
`;

const DrawerInfoValue = styled.span`
  color: #000;
  font-weight: 400;
  font-size: 16px;
  font-style: normal;
  line-height: 120%; 
  letter-spacing: -0.16px;
`;

const DrawerInfoRow = styled.div`
  display: flex;
  gap: 6px 12px;
  align-items: center;
`;

const DrawerInfoRowHalf = styled.div`
  display: flex;
  gap: 8px;
  min-width: 90px;
`;

const DrawerInfoRowLeftHalf = styled(DrawerInfoRowHalf)`
  width: 90px;
`;

const DrawerTabs = styled.div`
  margin-top: 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const DrawerTabHeader = styled.div`
  display: flex;
  gap: 32px;
  padding: 12px 20px;
  border-bottom: 1px solid #ededed;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
  }
`;

const DrawerTabButton = styled.button<{isActive: boolean}>`
  background: none;
  border: none;
  padding: 0;
  color: ${props => props.isActive ? "#448EF7" : "#000"};
  cursor: pointer;
  position: relative;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.16px;
  white-space: nowrap;
  flex: 0 0 auto;

  ${props => props.isActive ? `
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -12px;
      height: 3px;
      background: #448EF7;
    }
  ` : ""}
`;

const DrawerTabContent = styled.div`
  padding: 16px 20px;
`;

const DrawerTabContentEmpty = styled.div`
  min-height: 140px;
`;

const DrawerInfoColumns = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
    gap: 16px;
  }
`;

const DrawerInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 0;
`;

const DrawerInfoColumnEmpty = styled(DrawerInfoColumn)`
  padding: 0;
`;

const DrawerInfoLine = styled.div`
  display: flex;
  gap: 8px;
`;

const DrawerInfoLineLabel = styled.span`
  color: #808080;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.16px;
`;

const DrawerInfoLineValue = styled.span`
  color: #000;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.16px;
  text-transform: capitalize;
`;

const DrawerInfoLineValueBlue = styled(DrawerInfoLineValue)`
  color: #448EF7;
`

const DrawerInfoDivider = styled.div`
  width: 1px;
  background: #e5e5e5;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    width: 100%;
    height: 1px;
  }
`;

const DrawerSectionTitle = styled.div`
  color: #000;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.2px;
  margin-bottom: 8px;
`;

const DrawerSectionGroup = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
    gap: 16px;
  }
`;

const DrawerSectionColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 0;
`;

const AccessModalText = styled.div`
  text-align: center;
  font-size: 16px;
  color: #000;
  margin: 24px 0;
  color: #000;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
`;

const AccessModalIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    width: 100px;
    height: 100px;
  }
`;

const AccessModalActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: flex-end;
  flex-grow: 1;
`;

const AccessModalCancelButton = styled(CustomButton)`
  display: flex;
  height: 47px;
  padding: 5px 0;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  border-radius: 4px;
  border: 1px solid #1890FF;
  background: #FFF;
  color: #1890FF;
`;

const AccessModalSignUpButton = styled(CustomButton)`
 display: flex;
  height: 47px;
  padding: 5px 0;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  border-radius: 4px;
  border: 1px solid #1890FF;
  color: #FFF;
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px
`;

const AccessModalModal = styled(CustomModal)`
  .ant-modal-body {
    display: flex;
    flex-direction: column;
  }
`;

const AccessUnapprovedModalModal = styled(CustomModal)`
  .ant-modal-body {
    display: flex;
    flex-direction: column;
  }
`;

const AirportDataPageMessages = defineMessages({
  title: {
    id: "airportDataPage.title",
    defaultMessage: "Airport Data",
  },
  listTitle: {
    id: "airportDataPage.listTitle",
    defaultMessage: "Airport List",
  },
  download: {
    id: "airportDataPage.download",
    defaultMessage: "Download",
  },
  searchPlaceholder: {
    id: "airportDataPage.searchPlaceholder",
    defaultMessage: "Search airport: code, title, country, city",
  },
  placeholder: {
    id: "airportDataPage.placeholder",
    defaultMessage: "Airport data page is under construction.",
  },
  accessModalText: {
    id: "airportDataPage.accessModalText",
    defaultMessage: "Please register to access information",
  },
  accessModalClose: {
    id: "airportDataPage.accessModalClose",
    defaultMessage: "Close",
  },
  accessModalSignUp: {
    id: "airportDataPage.accessModalSignUp",
    defaultMessage: "Sign Up",
  },
  unapprovedModalText: {
    id: "airportDataPage.unapprovedModalText",
    defaultMessage: "Please wait until administrator approves your registration to access information",
  },
  errorLoadFailed: {
    id: "airportDataPage.errorLoadFailed",
    defaultMessage: "Failed to load airport data",
  },
  drawerTitle: {
    id: "airportDataPage.drawerTitle",
    defaultMessage: "Airport information",
  },
  tabGeneral: {
    id: "airportDataPage.tabGeneral",
    defaultMessage: "General information",
  },
  tabRunway: {
    id: "airportDataPage.tabRunway",
    defaultMessage: "Longest Runway",
  },
  tabFuel: {
    id: "airportDataPage.tabFuel",
    defaultMessage: "Fuel types",
  },
  tabCatering: {
    id: "airportDataPage.tabCatering",
    defaultMessage: "Catering Services",
  },
  labelIcao: {
    id: "airportDataPage.labelIcao",
    defaultMessage: "ICAO:",
  },
  labelLatitude: {
    id: "airportDataPage.labelLatitude",
    defaultMessage: "Latitude:",
  },
  labelIata: {
    id: "airportDataPage.labelIata",
    defaultMessage: "IATA:",
  },
  labelLongitude: {
    id: "airportDataPage.labelLongitude",
    defaultMessage: "Longitude:",
  },
  labelAirportType: {
    id: "airportDataPage.labelAirportType",
    defaultMessage: "Airport Type:",
  },
  labelLandingFacilityType: {
    id: "airportDataPage.labelLandingFacilityType",
    defaultMessage: "Landing Facility Type:",
  },
  labelSlotsRequired: {
    id: "airportDataPage.labelSlotsRequired",
    defaultMessage: "Slots Required:",
  },
  labelHandlingMandatory: {
    id: "airportDataPage.labelHandlingMandatory",
    defaultMessage: "Handling Mandatory:",
  },
  labelAirportOfEntry: {
    id: "airportDataPage.labelAirportOfEntry",
    defaultMessage: "Airport of Entry:",
  },
  labelTowerFrequency: {
    id: "airportDataPage.labelTowerFrequency",
    defaultMessage: "Tower Frequency:",
  },
  labelGndFrequency: {
    id: "airportDataPage.labelGndFrequency",
    defaultMessage: "GND Frequency:",
  },
  labelElevationFtMsl: {
    id: "airportDataPage.labelElevationFtMsl",
    defaultMessage: "Elevation (ft msl):",
  },
  labelHoursOfOperation: {
    id: "airportDataPage.labelHoursOfOperation",
    defaultMessage: "Hours of Operation:",
  },
  labelTimeZone: {
    id: "airportDataPage.labelTimeZone",
    defaultMessage: "Time Zone:",
  },
  labelUtcTime: {
    id: "airportDataPage.labelUtcTime",
    defaultMessage: "UTC time:",
  },
  labelInDaylightSaving: {
    id: "airportDataPage.labelInDaylightSaving",
    defaultMessage: "in Daylight Saving:",
  },
  labelLength: {
    id: "airportDataPage.labelLength",
    defaultMessage: "Length",
  },
  labelWidth: {
    id: "airportDataPage.labelWidth",
    defaultMessage: "Width",
  },
  labelUniversalId: {
    id: "airportDataPage.labelUniversalId",
    defaultMessage: "Universal ID",
  },
  labelSurface: {
    id: "airportDataPage.labelSurface",
    defaultMessage: "Surface",
  },
  unitFeet: {
    id: "airportDataPage.unitFeet",
    defaultMessage: "feet",
  },
  tabAirportInfo: {
    id: "airportDataPage.tabAirportInfo",
    defaultMessage: "Airport Info",
  },
  tabAirportCommunications: {
    id: "airportDataPage.tabAirportCommunications",
    defaultMessage: "Airport Communications",
  },
  tabFbosHandlingAgents: {
    id: "airportDataPage.tabFbosHandlingAgents",
    defaultMessage: "FBOs & Handling Agents",
  },
  tabHotels: {
    id: "airportDataPage.tabHotels",
    defaultMessage: "Hotels",
  },
  tabTransportationServices: {
    id: "airportDataPage.tabTransportationServices",
    defaultMessage: "Transportation services",
  },
  sectionAdditionalInformation: {
    id: "airportDataPage.sectionAdditionalInformation",
    defaultMessage: "Additional Information",
  },
  labelHasCustoms: {
    id: "airportDataPage.labelHasCustoms",
    defaultMessage: "Has customs:",
  },
  labelMagneticProperties: {
    id: "airportDataPage.labelMagneticProperties",
    defaultMessage: "Magnetic Properties:",
  },
  sectionLocation: {
    id: "airportDataPage.sectionLocation",
    defaultMessage: "Location",
  },
  labelDistanceToClosestCity: {
    id: "airportDataPage.labelDistanceToClosestCity",
    defaultMessage: "Distance to Closest City:",
  },
  labelDistanceToBusinessDistrict: {
    id: "airportDataPage.labelDistanceToBusinessDistrict",
    defaultMessage: "Distance to Business District:",
  },
  sectionManagerOwner: {
    id: "airportDataPage.sectionManagerOwner",
    defaultMessage: "Manager & Owner",
  },
  labelManager: {
    id: "airportDataPage.labelManager",
    defaultMessage: "Manager:",
  },
  labelManagerPhone: {
    id: "airportDataPage.labelManagerPhone",
    defaultMessage: "Manager Phone:",
  },
  sectionAirportCommunication: {
    id: "airportDataPage.sectionAirportCommunication",
    defaultMessage: "Airport Communication",
  },
  labelClrDelFrequency: {
    id: "airportDataPage.labelClrDelFrequency",
    defaultMessage: "CLR Del1 Frequency:",
  },
  labelApprFrequency: {
    id: "airportDataPage.labelApprFrequency",
    defaultMessage: "APPR Frequency:",
  },
  labelAtisFrequency: {
    id: "airportDataPage.labelAtisFrequency",
    defaultMessage: "Atis Frequency:",
  },
  labelAwosFrequency: {
    id: "airportDataPage.labelAwosFrequency",
    defaultMessage: "Awos Frequency:",
  },
  yes: {
    id: "airportDataPage.yes",
    defaultMessage: "Yes",
  },
  no: {
    id: "airportDataPage.no",
    defaultMessage: "No",
  },
});

export const AirportDataPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const userStatus = useAppSelector(state => state.user.oneItemObject.item.userStatus);
  const { width } = useWindowSize();
  const isNarrowDrawer = width <= 1032;
  const yesText = intl.formatMessage(AirportDataPageMessages.yes);
  const noText = intl.formatMessage(AirportDataPageMessages.no);
  const [inputValue, setInputValue] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasNoResults, setHasNoResults] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(InitialPage);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [airportsList, setAirportsList] = useState<any[]>([]);
  const pageSize = 20;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [drawerResponse, setDrawerResponse] = useState<any>(null);
  const [activeDrawerTab, setActiveDrawerTab] = useState<"general" | "runway" | "fuel" | "catering">("general");
  const [activeDrawerSecondaryTab, setActiveDrawerSecondaryTab] = useState<
    "airportInfo" | "communications" | "fbo" | "hotels" | "transportation"
  >("airportInfo");
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [isUnapprovedModalOpen, setIsUnapprovedModalOpen] = useState(false);
  const searchInputRef = useRef<any>(null);

  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => setSearchQuery(value), 1000),
    []
  );

  useEffect(() => {
    debouncedSetQuery(inputValue);
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [inputValue, debouncedSetQuery]);

  useEffect(() => {
    setCurrentPage(InitialPage);
  }, [searchQuery]);

  useEffect(() => {
    if (width <= screenSizes.mobile) {
      const t = setTimeout(() => {
        const el = searchInputRef.current?.input ?? searchInputRef.current;
        if (el && typeof el.focus === "function") el.focus();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [width]);

  useEffect(() => {
    let isCancelled = false;

    const runSearch = async () => {
      if (!searchQuery.trim()) {
        setSearchError(null);
        setIsLoading(false);
        setHasNoResults(false);
        setTotalCount(0);
        setAirportsList([]);
        return;
      }

      setIsLoading(true);
      setSearchError(null);

      try {
        // TODO: Keep Aviapages request for later use
        // const request = new AirportApiGetAirportFromAviapagesRequest();
        // request.setIcaocode(searchQuery.trim().toUpperCase());
        // const response = await AirportApi.getAirportFromAviapages(request);
        // const isSuccessful = response.getResultresponse()?.getIssuccessful();
        // const errorMessage = response.getResultresponse()?.getErrormessage();

        const request = new AirportApiAirportListRequest()
          .setQuery(searchQuery.trim())
          .setPagemodel(new PageModel().setPage(currentPage).setLimit(pageSize));

        const response = await AirportApi.getAirportList(request);

        if (isCancelled) return;

        const responseAirports = response.getAirportsList().map((airport) => airport.toObject());
        setTotalCount(response.getTotalcount());

        const nextAirportsList =
          currentPage === InitialPage ? responseAirports : [...airportsList, ...responseAirports];
        setAirportsList(nextAirportsList);
        setHasNoResults(nextAirportsList.length === 0);
        setSearchError(null);
      } catch (error: any) {
        if (isCancelled) return;
        setSearchError(error?.message || intl.formatMessage(AirportDataPageMessages.errorLoadFailed));
        setHasNoResults(false);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    runSearch();

    return () => {
      isCancelled = true;
    };
  }, [searchQuery, currentPage]);

  const loadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const formatUtcOffset = (offsetMinutes?: number) => {
    if (offsetMinutes === null || offsetMinutes === undefined) return "";
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absoluteMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
    const minutes = String(absoluteMinutes % 60).padStart(2, "0");
    return `UTC${sign}${hours}:${minutes}`;
  };

  const drawerAirport = drawerResponse?.airportextended?.airport;
  const drawerGeneral = drawerResponse?.airportextended?.airportgeneralinformation;
  const drawerUtc = drawerGeneral?.utctime || formatUtcOffset(drawerAirport?.timezone?.offset);

  const handleOpenDrawer = async (airport: any) => {
    if (!isAuthenticated) {
      setIsAccessModalOpen(true);
      return;
    }
    if (userStatus !== UserStatus.CONFIRMED) {
      setIsUnapprovedModalOpen(true);
      return;
    }
    setDrawerResponse(null);
    setDrawerError(null);
    setActiveDrawerTab("general");
    setActiveDrawerSecondaryTab("airportInfo");

    const icaoCode = airport?.icaocode?.trim();
    if (!icaoCode) {
      setDrawerError(intl.formatMessage(CommonFormMessages.noData));
      return;
    }

    setDrawerLoading(true);
    try {
      const request = new AirportApiGetAirportFromAviapagesRequest().setIcaocode(icaoCode);
      const response = await AirportApi.getAirportFromAviapages(request);
      setDrawerResponse(response.toObject());
      setIsDrawerOpen(true);
    } catch (error: any) {
      const errorParts = [
        error?.message,
        typeof error?.toString === "function" ? error.toString() : undefined,
        error?.response?.message,
        error?.response?.error?.message,
        error?.response?.error,
      ].filter(Boolean);
      const errorMessage = errorParts.join(" ");
      if (errorMessage.includes("USER_ACCOUNT_ERROR_UNAPPROVED")) {
        setDrawerError(null);
        setDrawerResponse(null);
        setIsUnapprovedModalOpen(true);
        setIsDrawerOpen(false);
        return;
      }
      setDrawerError(error?.message || intl.formatMessage(AirportDataPageMessages.errorLoadFailed));
    } finally {
      setDrawerLoading(false);
    }
  };

  return (
    <PageWrapper>
      <CustomCard width={'max'} isContainerCard={true}>
        <AirportDataHeader>
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            {intl.formatMessage(AirportDataPageMessages.listTitle)}
          </Typography.Title>
        </AirportDataHeader>
        <SearchRow gutter={8} align={"middle"}>
          <Col flex={1}>
            <CustomSearch
              ref={searchInputRef}
              placeholder={intl.formatMessage(AirportDataPageMessages.searchPlaceholder)}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onSearch={(value) => {
                setInputValue(value);
                setSearchQuery(value);
              }}
              loading={isLoading}
            />
          </Col>
        </SearchRow>
        {searchError && (
          <SearchError type="danger">
            {searchError}
          </SearchError>
        )}
        {!isLoading && hasNoResults && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
          </div>
        )}
        {airportsList.length > 0 && (
          <AirportList>
            {airportsList.map((airport) => {
              const codes = [airport?.icaocode, airport?.iatacode].filter(Boolean).join(", ");
              const countryName = airport?.countryname?.trim();
              const utcTime = formatUtcOffset(airport?.timezone?.offset);
              return (
                <AirportListItem key={airport.airportid} onClick={() => handleOpenDrawer(airport)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOpenDrawer(airport); } }}>
                  <AirportLeft>
                    {codes && <AirportCodes>{codes}</AirportCodes>}
                    <AirportName>{airport.name}</AirportName>
                  </AirportLeft>
                  <AirportMiddle>
                    {countryName && <AirportCountry>{countryName}</AirportCountry>}
                    {utcTime && <AirportUtc>{utcTime}</AirportUtc>}
                  </AirportMiddle>
                  <AirportRight>
                    <AirportArrow>›</AirportArrow>
                  </AirportRight>
                </AirportListItem>
              );
            })}
          </AirportList>
        )}
        {airportsList.length > 0 && totalCount > airportsList.length && (
          <CustomButton loading={isLoading} type="primary" onClick={loadMore}>
            {intl.formatMessage(CommonFormMessages.showMore)}
          </CustomButton>
        )}
      </CustomCard>
      <AiportDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        placement="right"
        width={isNarrowDrawer ? "100%" : 1032}
        contentMinWidth={isNarrowDrawer ? undefined : 1032}
        contentMaxWidth={isNarrowDrawer ? undefined : 1032}
      >
        <DrawerHeader>
          <DrawerTitle>{intl.formatMessage(AirportDataPageMessages.drawerTitle)}</DrawerTitle>
        </DrawerHeader>
        {drawerError && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
          </div>
        )}
        {drawerLoading && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <Loader />
          </div>
        )}
        {drawerResponse && drawerAirport && (
          <>
            <DrawerInfoCard>
              <DrawerInfoLeft>
                <DrawerInfoName>{drawerAirport.name}</DrawerInfoName>
                {(drawerAirport.countryname || drawerUtc) && (
                  <DrawerInfoMeta>
                    {[drawerAirport.countryname, drawerUtc].filter(Boolean).join(" ")}
                  </DrawerInfoMeta>
                )}
              </DrawerInfoLeft>
              <DrawerInfoDivider />
              <DrawerInfoRight>
                <DrawerInfoRow>
                  <DrawerInfoRowLeftHalf>
                    <DrawerInfoLabel>{intl.formatMessage(AirportDataPageMessages.labelIcao)}</DrawerInfoLabel>
                    <DrawerInfoValue>{drawerAirport.icaocode || "—"}</DrawerInfoValue>
                  </DrawerInfoRowLeftHalf>
                  <DrawerInfoRowHalf>
                    <DrawerInfoLabel>{intl.formatMessage(AirportDataPageMessages.labelLatitude)}</DrawerInfoLabel>
                    <DrawerInfoValue>{drawerAirport.latitude || "—"}</DrawerInfoValue>
                  </DrawerInfoRowHalf>
                </DrawerInfoRow>
                <DrawerInfoRow>
                  <DrawerInfoRowLeftHalf>
                    <DrawerInfoLabel>{intl.formatMessage(AirportDataPageMessages.labelIata)}</DrawerInfoLabel>
                    <DrawerInfoValue>{drawerAirport.iatacode || "—"}</DrawerInfoValue>
                  </DrawerInfoRowLeftHalf>
                  <DrawerInfoRowHalf>
                    <DrawerInfoLabel>{intl.formatMessage(AirportDataPageMessages.labelLongitude)}</DrawerInfoLabel>
                    <DrawerInfoValue>{drawerAirport.longitude || "—"}</DrawerInfoValue>
                  </DrawerInfoRowHalf>
                </DrawerInfoRow>
              </DrawerInfoRight>
            </DrawerInfoCard>
            <DrawerTabs>
              <DrawerTabHeader>
                <DrawerTabButton isActive={activeDrawerTab === "general"} onClick={() => setActiveDrawerTab("general")}>
                  {intl.formatMessage(AirportDataPageMessages.tabGeneral)}
                </DrawerTabButton>
                <DrawerTabButton isActive={activeDrawerTab === "runway"} onClick={() => setActiveDrawerTab("runway")}>
                  {intl.formatMessage(AirportDataPageMessages.tabRunway)}
                </DrawerTabButton>
                <DrawerTabButton isActive={activeDrawerTab === "fuel"} onClick={() => setActiveDrawerTab("fuel")}>
                  {intl.formatMessage(AirportDataPageMessages.tabFuel)}
                </DrawerTabButton>
                <DrawerTabButton isActive={activeDrawerTab === "catering"} onClick={() => setActiveDrawerTab("catering")}>
                  {intl.formatMessage(AirportDataPageMessages.tabCatering)}
                </DrawerTabButton>
              </DrawerTabHeader>
              <DrawerTabContent>
                {activeDrawerTab === "general" && (
                  <DrawerInfoColumns>
                    <DrawerInfoColumn>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelAirportType)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.airporttype || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelLandingFacilityType)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.landingfacilitytype || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelSlotsRequired)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.slotrequired ? yesText : noText}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelHandlingMandatory)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.handlingmandatory ? yesText : noText}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelAirportOfEntry)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.airporttoentry ? yesText : noText}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelTowerFrequency)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.towerfrequency || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                    </DrawerInfoColumn>
                    <DrawerInfoDivider />
                    <DrawerInfoColumn>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelGndFrequency)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.gndfrequency || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelElevationFtMsl)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.elevation || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelHoursOfOperation)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.hoursofoperation || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelTimeZone)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerAirport?.timezone?.name || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelUtcTime)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerUtc || "—"}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                      <DrawerInfoLine>
                        <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelInDaylightSaving)}</DrawerInfoLineLabel>
                        <DrawerInfoLineValue>{drawerGeneral?.indaylightsaving ? yesText : noText}</DrawerInfoLineValue>
                      </DrawerInfoLine>
                    </DrawerInfoColumn>
                  </DrawerInfoColumns>
                )}
                {activeDrawerTab === "runway" && (
                  <DrawerInfoColumn>
                    <DrawerInfoLine>
                      <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelLength)}</DrawerInfoLineLabel>
                      <DrawerInfoLineValue>
                        {drawerGeneral?.longestrunwaylength
                          ? `${drawerGeneral.longestrunwaylength} ${intl.formatMessage(AirportDataPageMessages.unitFeet)}`
                          : "—"}
                      </DrawerInfoLineValue>
                    </DrawerInfoLine>
                    <DrawerInfoLine>
                      <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelWidth)}</DrawerInfoLineLabel>
                      <DrawerInfoLineValue>
                        {drawerGeneral?.longestrunwaywidth
                          ? `${drawerGeneral.longestrunwaywidth} ${intl.formatMessage(AirportDataPageMessages.unitFeet)}`
                          : "—"}
                      </DrawerInfoLineValue>
                    </DrawerInfoLine>
                    <DrawerInfoLine>
                      <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelUniversalId)}</DrawerInfoLineLabel>
                      <DrawerInfoLineValue>{drawerGeneral?.longestrunwayuniversalid || "—"}</DrawerInfoLineValue>
                    </DrawerInfoLine>
                    <DrawerInfoLine>
                      <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelSurface)}</DrawerInfoLineLabel>
                      <DrawerInfoLineValue>{drawerGeneral?.longestrunwaysurface || "—"}</DrawerInfoLineValue>
                    </DrawerInfoLine>
                  </DrawerInfoColumn>
                )}
                {activeDrawerTab === "fuel" && (
                  <DrawerInfoColumnEmpty>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0' }}>
                      <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
                    </div>
                  </DrawerInfoColumnEmpty>
                )}
                {activeDrawerTab === "catering" && (
                  <DrawerInfoColumnEmpty>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0' }}>
                      <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
                    </div>
                  </DrawerInfoColumnEmpty>
                )}
              </DrawerTabContent>
            </DrawerTabs>
            <DrawerTabs>
              <DrawerTabHeader>
                <DrawerTabButton
                  isActive={activeDrawerSecondaryTab === "airportInfo"}
                  onClick={() => setActiveDrawerSecondaryTab("airportInfo")}
                >
                  {intl.formatMessage(AirportDataPageMessages.tabAirportInfo)}
                </DrawerTabButton>
                <DrawerTabButton
                  isActive={activeDrawerSecondaryTab === "communications"}
                  onClick={() => setActiveDrawerSecondaryTab("communications")}
                >
                  {intl.formatMessage(AirportDataPageMessages.tabAirportCommunications)}
                </DrawerTabButton>
                <DrawerTabButton
                  isActive={activeDrawerSecondaryTab === "fbo"}
                  onClick={() => setActiveDrawerSecondaryTab("fbo")}
                >
                  {intl.formatMessage(AirportDataPageMessages.tabFbosHandlingAgents)}
                </DrawerTabButton>
                <DrawerTabButton
                  isActive={activeDrawerSecondaryTab === "hotels"}
                  onClick={() => setActiveDrawerSecondaryTab("hotels")}
                >
                  {intl.formatMessage(AirportDataPageMessages.tabHotels)}
                </DrawerTabButton>
                <DrawerTabButton
                  isActive={activeDrawerSecondaryTab === "transportation"}
                  onClick={() => setActiveDrawerSecondaryTab("transportation")}
                >
                  {intl.formatMessage(AirportDataPageMessages.tabTransportationServices)}
                </DrawerTabButton>
              </DrawerTabHeader>
              <DrawerTabContent>
                {activeDrawerSecondaryTab === "airportInfo" && (
                  <>
                    <DrawerSectionGroup>
                      <DrawerSectionColumn>
                        <DrawerSectionTitle>{intl.formatMessage(AirportDataPageMessages.sectionAdditionalInformation)}</DrawerSectionTitle>
                        {drawerGeneral?.airportinfohascustomers !== undefined && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelHasCustoms)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral?.airportinfohascustomers ? yesText : noText}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                        {drawerGeneral?.airportinfomagneticproperties && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelMagneticProperties)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportinfomagneticproperties}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                      </DrawerSectionColumn>
                      <DrawerInfoDivider />
                      <DrawerSectionColumn>
                        {(drawerGeneral?.airportinfodistancetoclosestcity ||
                          drawerGeneral?.airportinfodistancetobusinessdistrict) && (
                          <>
                            <DrawerSectionTitle>{intl.formatMessage(AirportDataPageMessages.sectionLocation)}</DrawerSectionTitle>
                            {drawerGeneral?.airportinfodistancetoclosestcity && (
                              <DrawerInfoLine>
                                <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelDistanceToClosestCity)}</DrawerInfoLineLabel>
                                <DrawerInfoLineValue>{drawerGeneral.airportinfodistancetoclosestcity}</DrawerInfoLineValue>
                              </DrawerInfoLine>
                            )}
                            {drawerGeneral?.airportinfodistancetobusinessdistrict && (
                              <DrawerInfoLine>
                                <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelDistanceToBusinessDistrict)}</DrawerInfoLineLabel>
                                <DrawerInfoLineValue>{drawerGeneral.airportinfodistancetobusinessdistrict}</DrawerInfoLineValue>
                              </DrawerInfoLine>
                            )}
                          </>
                        )}
                      </DrawerSectionColumn>
                    </DrawerSectionGroup>
                    {(drawerGeneral?.airportinfomanagername || drawerGeneral?.airportinfomanagerphone) && (
                      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e5e5' }}>
                        <DrawerSectionTitle>{intl.formatMessage(AirportDataPageMessages.sectionManagerOwner)}</DrawerSectionTitle>
                        {drawerGeneral?.airportinfomanagername && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelManager)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportinfomanagername}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                        {drawerGeneral?.airportinfomanagerphone && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelManagerPhone)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValueBlue>{drawerGeneral.airportinfomanagerphone}</DrawerInfoLineValueBlue>
                          </DrawerInfoLine>
                        )}
                      </div>
                    )}
                  </>
                )}
                {activeDrawerSecondaryTab === "communications" && (
                  <>
                    {(drawerGeneral?.airportcommunicationtowerfrequency ||
                      drawerGeneral?.airportcommunicationgndfrequency ||
                      drawerGeneral?.airportcommunicationclrdelfrequency ||
                      drawerGeneral?.airportcommunicationapprfrequency ||
                      drawerGeneral?.airportcommunicationatisfrequency ||
                      drawerGeneral?.airportcommunicationawosfrequency) ? (
                      <DrawerInfoColumn>
                        <DrawerSectionTitle>{intl.formatMessage(AirportDataPageMessages.sectionAirportCommunication)}</DrawerSectionTitle>
                        {drawerGeneral?.airportcommunicationtowerfrequency && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelTowerFrequency)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportcommunicationtowerfrequency}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                        {drawerGeneral?.airportcommunicationgndfrequency && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelGndFrequency)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportcommunicationgndfrequency}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                        {drawerGeneral?.airportcommunicationclrdelfrequency && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelClrDelFrequency)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportcommunicationclrdelfrequency}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                        {drawerGeneral?.airportcommunicationapprfrequency && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelApprFrequency)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportcommunicationapprfrequency}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                        {drawerGeneral?.airportcommunicationatisfrequency && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelAtisFrequency)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportcommunicationatisfrequency}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                        {drawerGeneral?.airportcommunicationawosfrequency && (
                          <DrawerInfoLine>
                            <DrawerInfoLineLabel>{intl.formatMessage(AirportDataPageMessages.labelAwosFrequency)}</DrawerInfoLineLabel>
                            <DrawerInfoLineValue>{drawerGeneral.airportcommunicationawosfrequency}</DrawerInfoLineValue>
                          </DrawerInfoLine>
                        )}
                      </DrawerInfoColumn>
                    ) : (
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0' }}>
                        <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
                      </div>
                    )}
                  </>
                )}
                {activeDrawerSecondaryTab === "fbo" && (
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0' }}>
                    <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
                  </div>
                )}
                {activeDrawerSecondaryTab === "hotels" && (
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0' }}>
                    <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
                  </div>
                )}
                {activeDrawerSecondaryTab === "transportation" && (
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0' }}>
                    <CustomResult title={intl.formatMessage(CommonFormMessages.noData)} />
                  </div>
                )}
                {activeDrawerSecondaryTab !== "airportInfo" &&
                  activeDrawerSecondaryTab !== "communications" &&
                  activeDrawerSecondaryTab !== "fbo" &&
                  activeDrawerSecondaryTab !== "hotels" &&
                  activeDrawerSecondaryTab !== "transportation" && <DrawerTabContentEmpty />}
              </DrawerTabContent>
            </DrawerTabs>
          </>
        )}
      </AiportDrawer>
      <AccessModalModal
        open={isAccessModalOpen}
        onCancel={() => setIsAccessModalOpen(false)}
        footer={null}
        centered
        width={450}
        bodyStyle={{ minHeight: 280 }}
      >
        <AccessModalIcon>
          <WarningIcon />
        </AccessModalIcon>
        <AccessModalText>{intl.formatMessage(AirportDataPageMessages.accessModalText)}</AccessModalText>
        <AccessModalActions>
          <AccessModalCancelButton type="default" onClick={() => setIsAccessModalOpen(false)}>
            {intl.formatMessage(AirportDataPageMessages.accessModalClose)}
          </AccessModalCancelButton>
          <AccessModalSignUpButton type="primary" onClick={() => navigate(pathFromRoute(Route.register))}>
            {intl.formatMessage(AirportDataPageMessages.accessModalSignUp)}
          </AccessModalSignUpButton>
        </AccessModalActions>
      </AccessModalModal>
      <AccessUnapprovedModalModal
        open={isUnapprovedModalOpen}
        onCancel={() => setIsUnapprovedModalOpen(false)}
        footer={null}
        centered
        width={450}
        bodyStyle={{ minHeight: 280 }}
      >
        <AccessModalIcon>
          <WarningIcon />
        </AccessModalIcon>
        <AccessModalText>{intl.formatMessage(AirportDataPageMessages.unapprovedModalText)}</AccessModalText>
        <AccessModalActions>
          <AccessModalCancelButton type="default" onClick={() => setIsUnapprovedModalOpen(false)}>
            {intl.formatMessage(AirportDataPageMessages.accessModalClose)}
          </AccessModalCancelButton>
          <AccessModalSignUpButton type="primary" onClick={() => navigate(pathFromRoute(Route.register))}>
            {intl.formatMessage(AirportDataPageMessages.accessModalSignUp)}
          </AccessModalSignUpButton>
        </AccessModalActions>
      </AccessUnapprovedModalModal>
    </PageWrapper>
  );
};
